import React, { useState, useEffect } from 'react';
import { Plus, Minus, Check, X, ShieldCheck, Truck, Lock, Sparkles, Info, RotateCcw } from 'lucide-react';
import Accordion from '../components/Accordion';
import Stars from '../components/Stars';
import ProductGallery from '../components/ProductGallery';
import ProductVideos from '../components/ProductVideos';
import PaymentIcons from '../components/PaymentIcons';
import PaymentIconsInline from '../components/PaymentIconsInline';
import BuyNowPayLater from '../components/BuyNowPayLater';
import LooxReviews from '../components/LooxReviews';
import FreeShippingBadge from '../components/FreeShippingBadge';
import CouponReveal from '../components/CouponReveal';
import { useCurrency } from '../components/CurrencyContext';
import { useCart } from '../components/CartContext';
import { fbViewContent, fbAddToCart } from '../lib/metaPixel';
import {
  PRODUCT,
  PRODUCT_VIDEOS,
  GALLERY_HERO_VIDEO,
  PRODUCT_FAQS,
  PRODUCT_REVIEWS,
  HOW_IT_WORKS,
  STATS,
} from '../mock';
import { toast } from 'sonner';

const BundleOption = ({ bundle, selected, onSelect, formatPrice, discountPct = 0 }) => {
  const isSelected = selected === bundle.id;
  const isHero = bundle.is_hero;
  const hasDiscount = discountPct > 0;
  const discountedPrice = hasDiscount
    ? Number((bundle.price * (1 - discountPct / 100)).toFixed(2))
    : bundle.price;
  return (
    <div className="relative">
      {(bundle.tag || isHero) && (
        <div className="absolute -top-3 right-3 z-10">
          <div
            className="px-3 py-1 text-[10px] tracking-wide uppercase font-semibold relative"
            style={{
              backgroundColor: isHero && !bundle.tag ? '#D9A441' : '#2D5A3D',
              color: '#FFFFFF',
              borderRadius: '6px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
          >
            {bundle.tag || 'FEATURED OFFER'}
            <span
              className="absolute -bottom-[5px] right-3 w-0 h-0"
              style={{
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                borderTop: `6px solid ${isHero && !bundle.tag ? '#D9A441' : '#2D5A3D'}`,
              }}
            />
          </div>
        </div>
      )}
      <button
        onClick={() => onSelect(bundle.id)}
        className="relative w-full text-left rounded-2xl px-3 py-4 md:px-4 flex items-center gap-3 btn-hover"
        style={{
          border: isSelected ? '2px solid #2D5A3D' : '2px solid rgba(45,90,61,0.18)',
          backgroundColor: '#FFFFFF',
        }}
      >
        <div
          className="w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center"
          style={{ borderColor: '#2D5A3D', backgroundColor: isSelected ? '#2D5A3D' : 'transparent' }}
        >
          {isSelected && <span className="block w-2 h-2 rounded-full bg-white" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[16px] font-bold leading-tight" style={{ color: '#000000' }}>
              {bundle.label}
            </p>
            <span className="text-[12px] px-2.5 py-0.5 rounded-md font-medium" style={{ backgroundColor: '#F5EDE0', color: '#2C2418' }}>
              {bundle.bars}
            </span>
          </div>
          <p className="text-[13px] italic mt-1" style={{ color: '#9CA3A2' }}>
            {bundle.duration}
          </p>
        </div>
        <div className="text-right shrink-0">
          {hasDiscount ? (
            <>
              <p className="text-[16px] font-bold leading-none" style={{ color: '#2D5A3D' }}>
                {formatPrice(discountedPrice)}
              </p>
              <p className="text-[12px] line-through opacity-60 mt-0.5" style={{ color: '#000000' }}>
                {formatPrice(bundle.price)}
              </p>
            </>
          ) : (
            <p className="text-[16px] font-bold" style={{ color: '#000000' }}>
              {formatPrice(bundle.price)}
            </p>
          )}
          {bundle.tax_inclusive && (
            <p className="text-[10px] italic" style={{ color: '#2D5A3D' }}>
              VAT incl.
            </p>
          )}
        </div>
      </button>
    </div>
  );
};

const Product = () => {
  const { bundles: ctxBundles, formatPrice, currency, taxInclusive } = useCurrency();
  const bundles = (ctxBundles && ctxBundles.length ? ctxBundles : PRODUCT.bundles);
  const [selectedBundle, setSelectedBundle] = useState('b1');
  const [qty, setQty] = useState(1);
  const [coupon, setCoupon] = useState(null);
  const { addItem } = useCart();

  const bundle = bundles.find((b) => b.id === selectedBundle) || bundles[0];
  const heroBundle = bundles.find((b) => b.is_hero) || bundles[0];

  const discountFactor = coupon ? (1 - coupon.percent_off / 100) : 1;
  const unitPriceWithCoupon = Number((bundle.price * discountFactor).toFixed(2));
  const totalWithCoupon = Number((unitPriceWithCoupon * qty).toFixed(2));

  // Meta Pixel: ViewContent on product page mount (fires once per mount)
  useEffect(() => {
    fbViewContent({
      id: PRODUCT.id,
      name: PRODUCT.title,
      price: heroBundle?.price,
      currency,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = () => {
    addItem({
      id: `${PRODUCT.id}-${bundle.id}`,
      title: PRODUCT.title,
      variant: `${bundle.label} \u00b7 ${bundle.bars}`,
      price: bundle.price,
      currency: bundle.currency || currency,
      symbol: bundle.symbol || '$',
      image: PRODUCT.images[0],
      qty,
    });
    fbAddToCart({
      id: `${PRODUCT.id}-${bundle.id}`,
      name: `${PRODUCT.title} — ${bundle.label}`,
      price: bundle.price,
      currency: bundle.currency || currency,
      quantity: qty,
    });
    toast.success('Added to cart');
  };

  return (
    <main style={{ backgroundColor: '#FAFAF7' }}>
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-12 py-6 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-start">
          {/* Gallery (sticky on desktop until right column ends) */}
          <div className="lg:sticky lg:top-[112px] self-start">
            <ProductGallery
              items={[
                // Hero video — autoplay muted on mount for max CVR impact
                {
                  type: 'video',
                  src: GALLERY_HERO_VIDEO.src,
                  poster: GALLERY_HERO_VIDEO.poster,
                  alt: 'K-Root Korean Scalp Revival Bar — in use',
                },
                ...PRODUCT.images.map((src) => ({ type: 'image', src })),
              ]}
            />
          </div>

          {/* Details */}
          <div>
            <div className="flex items-center gap-2 text-[13px]" style={{ color: '#2C2418' }}>
              <Stars rating={PRODUCT.rating} size={14} color="#D9A441" />
              <span className="opacity-80">{PRODUCT.rating}/5 ({PRODUCT.reviewCount} reviews)</span>
            </div>
            <h1 className="font-serif text-[40px] md:text-[48px] mt-3 leading-[1.05] font-medium" style={{ color: '#2C2418' }}>
              {PRODUCT.title}
            </h1>

            {/* TIME-PROMISE sub-headline (hook #1) */}
            <p
              className="mt-2 font-serif italic text-[16px] md:text-[18px] leading-snug"
              style={{ color: '#2D5A3D' }}
            >
              Week 3: less in the drain. Week 6: baby hairs. Week 10: a part you stop hiding.
            </p>

            {/* Hero offer price */}
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <span className="text-[24px] font-bold" style={{ color: '#2C2418' }}>
                {formatPrice ? formatPrice(heroBundle.price) : `$${heroBundle.price.toFixed(2)}`}
              </span>
              <span className="text-[12px] tracking-wide px-2.5 py-1 rounded-lg font-semibold" style={{ backgroundColor: '#D9A441', color: '#FFFFFF' }}>
                FEATURED OFFER
              </span>
              <span className="text-[12px] italic" style={{ color: '#2D5A3D' }}>
                {heroBundle.label} · {heroBundle.bars}
              </span>
            </div>

            {/* Free shipping badge — diagonal oblique tag with icon */}
            <div className="mt-4">
              <FreeShippingBadge variant="oblique" />
            </div>

            {/* Features pills (cream beige) */}
            <div className="mt-5 flex flex-wrap gap-2 justify-center sm:justify-start">
              {PRODUCT.features.map((f) => (
                <span key={f} className="text-[12px] px-3 py-1.5 rounded-full" style={{ backgroundColor: '#F5EDE0', color: '#2C2418' }}>
                  {f}
                </span>
              ))}
            </div>

            <p className="mt-5 italic text-[14px] leading-relaxed" style={{ color: '#2C2418' }}>
              {PRODUCT.shortDescription}
            </p>

            {/* Bundles container */}
            <div className="mt-7">
              <p className="text-[15px] font-bold text-center mb-5" style={{ color: '#000000' }}>
                Choose your bundle — Free Shipping Worldwide
              </p>

              {/* Coupon reveal field (auto-fills from welcome popup) */}
              <CouponReveal onApplied={setCoupon} applied={coupon} bundles={bundles} />

              <div className="space-y-5 pt-2">
                {bundles.map((b) => (
                  <BundleOption
                    key={b.id}
                    bundle={b}
                    selected={selectedBundle}
                    onSelect={setSelectedBundle}
                    formatPrice={formatPrice || ((p) => `$${Number(p).toFixed(2)}`)}
                    discountPct={coupon?.percent_off || 0}
                  />
                ))}
              </div>

              {/* Tax / pricing notes */}
              {taxInclusive ? (
                <div className="mt-4 flex items-start gap-2 text-[12px]" style={{ color: '#2D5A3D' }}>
                  <Info size={14} className="mt-0.5 shrink-0" />
                  <p>VAT included. The price on the bundle is the price you pay.</p>
                </div>
              ) : currency === 'CAD' ? (
                <div className="mt-4 flex items-start gap-2 text-[12px]" style={{ color: '#2D5A3D' }}>
                  <Info size={14} className="mt-0.5 shrink-0" />
                  <p>Prices shown pre-tax. GST/PST applied at checkout — that is it.</p>
                </div>
              ) : (
                <div className="mt-4 flex items-start gap-2 text-[12px]" style={{ color: '#2D5A3D' }}>
                  <Info size={14} className="mt-0.5 shrink-0" />
                  <p>Free shipping, no customs surprises, no junk fees at checkout.</p>
                </div>
              )}
            </div>

            {/* Honest expectation micro-copy right above the CTA (hook #2) */}
            <p className="mt-6 text-[12px] italic leading-snug text-center sm:text-left" style={{ color: '#6B7765' }}>
              Most women feel the shedding slow inside three weeks. The mirror catches up about four weeks after that.
            </p>

            {/* Qty + Add */}
            <div className="mt-2 flex items-stretch gap-3">
              <div className="inline-flex items-center border rounded-lg" style={{ borderColor: 'rgba(45,90,61,0.3)' }}>
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-full flex items-center justify-center" style={{ color: '#2D5A3D' }} aria-label="Decrease">
                  <Minus size={16} />
                </button>
                <span className="w-10 text-center font-semibold" style={{ color: '#2C2418' }}>{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="w-11 h-full flex items-center justify-center" style={{ color: '#2D5A3D' }} aria-label="Increase">
                  <Plus size={16} />
                </button>
              </div>
              <button
                onClick={handleAdd}
                className="flex-1 py-[18px] rounded-full text-[14px] uppercase tracking-[0.16em] font-bold btn-hover hover:opacity-95"
                style={{ backgroundColor: '#2D5A3D', color: '#FFFFFF', boxShadow: '0 4px 14px rgba(45,90,61,0.28)' }}
              >
                Start My Revival Today
              </button>
            </div>

            {/* Buy Now Pay Later (Klarna / Afterpay) */}
            <BuyNowPayLater total={coupon ? totalWithCoupon : bundle.price * qty} currency={currency} symbol={bundle.symbol} />

            {/* Inline payment icons directly under CTA — visible BEFORE click */}
            <div className="mt-3">
              <PaymentIconsInline />
            </div>

            {/* Expanded 60-Day Guarantee badge right under the buy box (max trust moment) */}
            <div
              className="mt-4 p-4 rounded-2xl flex items-start gap-3"
              style={{ backgroundColor: '#F5EDE0' }}
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: '#2D5A3D' }}>
                <ShieldCheck size={20} color="#FFFFFF" />
              </div>
              <div className="flex-1">
                <p className="text-[14px] font-bold leading-tight" style={{ color: '#2C2418' }}>
                  60 Days. Full Refund. No Hoops.
                </p>
                <p className="text-[13px] mt-0.5 leading-snug" style={{ color: '#2C2418' }}>
                  Try the ritual for two months. If you do not feel the shedding slow down, email us — full refund, even on a bar you have already used.
                </p>
              </div>
            </div>

            {/* Compact trust row: free shipping + secure checkout + returns */}
            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              {[
                { icon: Truck, label: 'Free Worldwide Shipping' },
                { icon: Lock, label: 'Secure Stripe Checkout' },
                { icon: RotateCcw, label: 'Easy 60-Day Returns' },
              ].map((t, i) => {
                const Icon = t.icon;
                return (
                  <div key={i} className="flex flex-col items-center gap-1.5" style={{ color: '#2C2418' }}>
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#ECECE9' }}>
                      <Icon size={20} strokeWidth={1.7} color="#2C2418" />
                    </div>
                    <span className="text-[11px] leading-tight">{t.label}</span>
                  </div>
                );
              })}
            </div>

            <p className="mt-6 text-center text-[12px] tracking-[0.2em] uppercase opacity-80" style={{ color: '#2D5A3D' }}>
              10,000+ scalps revived and counting
            </p>

            {/* Inline product videos row (right column) */}
            <ProductVideos compact />

            {/* HOW IT WORKS — visible 3-step block (no click required) */}
            <section className="mt-10">
              <h3 className="font-serif text-[24px] md:text-[28px] mb-1" style={{ color: '#2C2418' }}>
                The 60-second ritual
              </h3>
              <p className="text-[13px] mb-5 italic" style={{ color: '#2D5A3D' }}>
                One minute in your shower. Eight weeks to a part you do not have to hide. That is the entire promise.
              </p>
              <ol className="grid grid-cols-3 gap-3">
                {HOW_IT_WORKS.map((s, i) => (
                  <li
                    key={s.id}
                    className="relative p-4 rounded-2xl flex flex-col items-center text-center"
                    style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(45,90,61,0.12)' }}
                  >
                    <div
                      className="absolute -top-3 left-3 w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold"
                      style={{ backgroundColor: '#2D5A3D', color: '#FFFFFF' }}
                    >
                      {i + 1}
                    </div>
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-[110px] object-cover rounded-xl mb-3"
                    />
                    <p className="text-[13px] font-bold leading-tight" style={{ color: '#2C2418' }}>
                      {s.title}
                    </p>
                    <p className="text-[12px] mt-1 leading-snug opacity-80" style={{ color: '#2C2418' }}>
                      {s.body}
                    </p>
                  </li>
                ))}
              </ol>
            </section>

            <div className="mt-8">
              <Accordion items={PRODUCT_FAQS} />
            </div>
          </div>
        </div>

        {/* Persona bridge banner (hook #4) — primes the postpartum/perimenopausia reader */}
        <section className="mt-20">
          <div
            className="max-w-[820px] mx-auto px-6 py-8 md:py-10 rounded-3xl text-center"
            style={{ backgroundColor: '#F5EDE0' }}
          >
            <p className="text-[11px] uppercase tracking-[0.22em] mb-3 opacity-75" style={{ color: '#2D5A3D' }}>
              Built for the chapter that took your hair
            </p>
            <p className="font-serif italic text-[24px] md:text-[30px] leading-[1.2]" style={{ color: '#2C2418' }}>
              "Postpartum. Perimenopause. The job that broke you. K-Root is the bar for the moment your body went quiet — and it is time to bring you back."
            </p>
          </div>
        </section>

        {/* Loox-style customer reviews */}
        <LooxReviews />

        {/* Reviews highlight */}
        <section className="mt-20">
          <h2 className="font-serif text-[32px] md:text-[40px] text-center" style={{ color: '#2D5A3D' }}>
            10,000+ women. Real photos. Real regrowth.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PRODUCT_REVIEWS.map((r) => (
              <article key={r.id} className="rounded-2xl overflow-hidden border"
                style={{ borderColor: 'rgba(45,90,61,0.12)', backgroundColor: '#F5EDE0' }}>
                <img src={r.image} alt={r.name} className="w-full h-[260px] object-cover" />
                <div className="p-5">
                  <Stars rating={r.rating} size={14} color="#D9A441" />
                  <h4 className="font-serif text-[20px] mt-2" style={{ color: '#2D5A3D' }}>{r.name}</h4>
                  <p className="mt-2 text-[14px] leading-relaxed" style={{ color: '#2D5A3D' }}>{r.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Thicker hair */}
        <section className="mt-24 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif text-[32px] md:text-[42px] leading-tight" style={{ color: '#2D5A3D' }}>
              The growth was never going to come from a bottle.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed" style={{ color: '#2D5A3D' }}>
              Western haircare polishes the strand. K-Root rebuilds the soil. A traditional shampoo is up to 90% water — by the time it reaches your scalp it has already been diluted to almost nothing. K-Root is solid, concentrated, fermented. Twelve Korean botanicals press directly into the follicle, dissolve the buildup that was strangling growth, calm the inflammation that triggered the shedding, and clear the path for the hair your body has been trying to grow all along.
            </p>
            <ul className="mt-6 space-y-2">
              {[
                'Visible regrowth in 6–8 weeks',
                '100% plant-derived, zero filler',
                'One bar = up to 60 washes',
              ].map((f) => (
                <li key={f} className="flex items-center gap-2 text-[14px]" style={{ color: '#2D5A3D' }}>
                  <Check size={16} /> {f}
                </li>
              ))}
            </ul>
          </div>
          <img
            src="https://customer-assets.emergentagent.com/job_content-135/artifacts/kf9mto3g_hf_20260305_003409_75b91e88-6e02-44f1-9c8d-787e9b8d1b62-REPLACE.png"
            alt=""
            className="w-full h-auto rounded-2xl"
          />
        </section>

        {/* How it works section is now visible inline in the right column above the FAQ */}

        {/* Stats */}
        <section className="mt-20">
          {/* Visual time-promise headline above the stats (hook #5) */}
          <h2 className="font-serif text-[28px] md:text-[40px] text-center mb-2 leading-[1.1]" style={{ color: '#2C2418' }}>
            Week 4: half the hair in the drain.<br />
            Week 8: twice the hair in the mirror.
          </h2>
          <p className="text-[13px] text-center mb-10 opacity-75" style={{ color: '#2C2418' }}>
            Verified self-reported results from 1,248 K-Root customers after eight weeks of daily use.
          </p>
          <div className="py-14 px-6 rounded-2xl text-center" style={{ backgroundColor: '#2D5A3D', color: '#F5EDDF' }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {STATS.map((s) => (
                <div key={s.label}>
                  <p className="font-serif text-[52px] md:text-[64px] leading-none">{s.value}</p>
                  <p className="mt-3 text-[14px] opacity-90">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why K-Root comparison */}
        <section className="mt-20">
          <h2 className="font-serif text-[32px] md:text-[40px] text-center mb-10" style={{ color: '#2D5A3D' }}>
            K-Root vs. the bottle on your shelf.
          </h2>
          <div className="max-w-[720px] mx-auto rounded-2xl overflow-hidden border" style={{ borderColor: 'rgba(45,90,61,0.15)' }}>
            <div className="grid grid-cols-3" style={{ backgroundColor: '#F5EDDF' }}>
              <div className="p-4"></div>
              <div className="p-4 text-center font-serif italic text-[22px]" style={{ color: '#2D5A3D' }}>K-Root</div>
              <div className="p-4 text-center text-[14px] opacity-80" style={{ color: '#2D5A3D' }}>Others</div>
            </div>
            {[
              'Sulfate-free',
              '12 fermented herbs',
              '60-sec scalp ritual',
              '0% water filler',
              'Vegan & cruelty-free',
              'Visible results',
            ].map((row, idx) => (
              <div
                key={row}
                className="grid grid-cols-3 border-t"
                style={{ borderColor: 'rgba(45,90,61,0.12)', backgroundColor: idx % 2 === 0 ? '#F5EDE0' : 'white' }}
              >
                <div className="p-4 text-[14px]" style={{ color: '#2D5A3D' }}>{row}</div>
                <div className="p-4 flex justify-center"><Check size={18} color="#2D5A3D" /></div>
                <div className="p-4 flex justify-center"><X size={18} color="#9b6d6d" /></div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
};

export default Product;
